import { ref } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';

const API_BASE = (process.env.VUE_APP_API_URL || 'http://localhost:3333').replace(/\/$/, '');

export function usePermissions() {
  const router = useRouter();
  const authStore = useAuthStore();
  const token = authStore?.token || null;
  
  const userPermissions = ref([]);
  const isLoading = ref(false);
  const userDetails = ref(null);

  // Função para buscar detalhes do usuário
  const fetchUserDetails = async () => {
    if (!token) {
      router.push('/access-denied');
      return null;
    }

    try {
      isLoading.value = true;
      const response = await axios.get(`${API_BASE}/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        userDetails.value = response.data;
        userPermissions.value = response.data.allowedScreens || [];
        return response.data;
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes do usuário:', error);
      router.push('/access-denied');
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  // Função para verificar se o usuário tem permissão para uma tela específica
  const hasPermission = (screenName) => {
    return userPermissions.value.includes(screenName);
  };

  // Função para verificar permissão e redirecionar se necessário
  const checkPermissionAndRedirect = async (requiredScreen) => {
    // Se já temos as permissões carregadas, verifica diretamente
    if (userPermissions.value.length > 0) {
      if (!hasPermission(requiredScreen)) {
        router.push('/access-denied');
        return false;
      }
      return true;
    }

    // Se não temos as permissões, busca do servidor
    const user = await fetchUserDetails();
    if (!user) {
      return false;
    }

    if (!hasPermission(requiredScreen)) {
      router.push('/access-denied');
      return false;
    }

    return true;
  };

  // Função para verificar múltiplas permissões
  const hasAnyPermission = (screenNames) => {
    return screenNames.some(screen => hasPermission(screen));
  };

  // Função para verificar se tem todas as permissões
  const hasAllPermissions = (screenNames) => {
    return screenNames.every(screen => hasPermission(screen));
  };

  return {
    userPermissions,
    userDetails,
    isLoading,
    fetchUserDetails,
    hasPermission,
    checkPermissionAndRedirect,
    hasAnyPermission,
    hasAllPermissions
  };
}
